// ────────────────────────────────────────────────────────────
// API client for AIBens.CC publishing platform
// All requests go to the Cloudflare Worker via /api/*
// ────────────────────────────────────────────────────────────

const BASE: string = import.meta.env.VITE_API_BASE ?? ''

// Token is stored in localStorage after successful OTP verification
const TOKEN_KEY = 'aibens_token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

// ────────────────────────────────────────────────────────────
// Core fetch wrapper
// ────────────────────────────────────────────────────────────

interface FetchOptions extends RequestInit {
  json?: unknown
}

async function apiFetch<T = unknown>(
  path: string,
  opts: FetchOptions = {}
): Promise<T> {
  const { json, headers: extraHeaders, ...rest } = opts
  const headers: Record<string, string> = {
    ...(extraHeaders as Record<string, string>),
  }

  const token = getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  if (json !== undefined) {
    headers['Content-Type'] = 'application/json'
    rest.body = JSON.stringify(json)
  }

  const res = await fetch(`${BASE}${path}`, { ...rest, headers })

  if (!res.ok) {
    let message = `HTTP ${res.status}`
    try {
      const errBody = (await res.json()) as { error?: string }
      if (errBody.error) message = errBody.error
    } catch {
      // ignore parse errors
    }
    throw new Error(message)
  }

  // 204 No Content
  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

// ────────────────────────────────────────────────────────────
// Auth
// ────────────────────────────────────────────────────────────

export interface User {
  id: string
  phone: string | null
  email: string | null
  role: 'admin' | 'user'
  expiresAt?: string
}

export async function sendOtp(phone: string): Promise<{ ok: boolean }> {
  return apiFetch('/api/auth/send-otp', {
    method: 'POST',
    json: { phone },
  })
}

export async function verifyOtp(
  phone: string,
  code: string
): Promise<{ ok: boolean; token: string; user: User }> {
  const result = await apiFetch<{ ok: boolean; token: string; user: User }>(
    '/api/auth/verify-otp',
    { method: 'POST', json: { phone, code } }
  )
  // Persist token
  setToken(result.token)
  return result
}

export async function sendEmailOtp(email: string): Promise<{ ok: boolean; dev_code?: string }> {
  return apiFetch('/api/auth/send-email-otp', { method: 'POST', json: { email } })
}

export async function verifyEmailOtp(
  email: string,
  code: string
): Promise<{ ok: boolean; token: string; user: User }> {
  const result = await apiFetch<{ ok: boolean; token: string; user: User }>(
    '/api/auth/verify-email-otp',
    { method: 'POST', json: { email, code } }
  )
  setToken(result.token)
  return result
}

export async function getWechatQr(): Promise<{ state: string; qrUrl: string }> {
  return apiFetch('/api/auth/wechat/qr')
}

export async function pollWechatLogin(state: string): Promise<{ ok: boolean; token?: string }> {
  return apiFetch(`/api/auth/wechat/poll?state=${encodeURIComponent(state)}`)
}

export async function passwordLogin(
  username: string,
  password: string
): Promise<{ ok: boolean; token: string; user: User }> {
  const result = await apiFetch<{ ok: boolean; token: string; user: User }>(
    '/api/auth/password-login',
    { method: 'POST', json: { username, password } }
  )
  setToken(result.token)
  return result
}

export async function logout(): Promise<void> {
  await apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => {})
  clearToken()
}

export async function getMe(): Promise<User> {
  return apiFetch('/api/auth/me')
}

// ────────────────────────────────────────────────────────────
// Content
// ────────────────────────────────────────────────────────────

export interface ContentItem {
  id: string
  slug: string
  title: string
  type: 'html' | 'pdf' | 'ppt'
  r2_key: string
  tags: string[]
  description: string
  size_bytes: number
  uploader_id: string
  archived: boolean
  created_at: string
  updated_at: string
}

export interface ContentDetail extends ContentItem {
  permission: {
    id: string
    access_type: 'logged_in' | 'phone_list' | 'admin_only' | 'public' | 'password'
    phone_list: string[]
    expires_at: string | null
  } | null
}

export interface PaginatedContent {
  items: ContentItem[]
  total: number
  page: number
  pageSize: number
}

export interface ListContentOpts {
  q?: string
  type?: string
  tag?: string
  archived?: boolean
  page?: number
  pageSize?: number
}

export async function listContent(
  opts: ListContentOpts = {}
): Promise<PaginatedContent> {
  const params = new URLSearchParams()
  if (opts.q) params.set('q', opts.q)
  if (opts.type) params.set('type', opts.type)
  if (opts.tag) params.set('tag', opts.tag)
  if (opts.archived) params.set('archived', 'true')
  if (opts.page) params.set('page', String(opts.page))
  if (opts.pageSize) params.set('pageSize', String(opts.pageSize))
  const qs = params.toString()
  return apiFetch(`/api/content${qs ? `?${qs}` : ''}`)
}

export async function getContent(id: string): Promise<ContentDetail> {
  return apiFetch(`/api/content/${id}`)
}

export interface UploadContentInput {
  title: string
  type: 'html' | 'pdf' | 'ppt'
  content: string // raw text for HTML, base64 for binary
  tags?: string[]
  description?: string
  slug?: string
  permission?: {
    access_type?: 'logged_in' | 'phone_list' | 'admin_only'
    phone_list?: string[]
    expires_at?: string | null
  }
}

export interface UploadResult {
  id: string
  slug: string
  url: string
  type: string
  title: string
}

export async function uploadContent(
  data: UploadContentInput
): Promise<UploadResult> {
  return apiFetch('/api/content/upload', { method: 'POST', json: data })
}

export async function uploadContentFile(
  formData: FormData
): Promise<UploadResult> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${BASE}/api/content/upload`, {
    method: 'POST',
    headers,
    body: formData,
  })
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(err.error ?? `HTTP ${res.status}`)
  }
  return res.json()
}

export async function updateContent(
  id: string,
  data: Partial<{
    title: string
    tags: string[]
    description: string
    permission: {
      access_type?: 'logged_in' | 'phone_list' | 'admin_only' | 'public' | 'password'
      phone_list?: string[]
      expires_at?: string | null
    }
  }>
): Promise<ContentDetail> {
  return apiFetch(`/api/content/${id}`, { method: 'PATCH', json: data })
}

export async function deleteContent(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/content/${id}`, { method: 'DELETE' })
}

export async function archiveContent(
  id: string
): Promise<{ ok: boolean; archived: boolean }> {
  return apiFetch(`/api/content/${id}/archive`, { method: 'POST' })
}

export interface AccessLog {
  id: string
  content_id: string
  user_id: string | null
  phone: string | null
  ip: string | null
  user_agent: string | null
  accessed_at: string
}

export async function getAccessLogs(
  id: string
): Promise<{ logs: AccessLog[] }> {
  return apiFetch(`/api/content/${id}/logs`)
}
